import Component from 'flarum/common/Component';
import icon from 'flarum/common/helpers/icon';
import classList from 'flarum/common/utils/classList';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import batchManager from '../utils/batch-manager';
import type Mithril from 'mithril';

interface LinkPreviewAttrs {
  link: HTMLAnchorElement;
  openLinksInNewTab: boolean;
  useGoogleFavicons: boolean;
  previewId: string;
}

interface LinkPreviewData {
  title?: string;
  description?: string;
  site_name?: string;
  image?: string;
  error?: string;
}

export default class LinkPreview extends Component<LinkPreviewAttrs> {
  loading!: boolean;
  link!: HTMLAnchorElement;
  linkAttributes!: { [key: string]: string };
  linkClasses!: string;
  data!: LinkPreviewData | null;
  useGoogleFavicons!: boolean;
  previewId!: string;

  oninit(vnode: Mithril.Vnode<LinkPreviewAttrs, this>) {
    super.oninit(vnode);

    const attrs = vnode.attrs;

    this.loading = true;
    this.link = attrs.link;
    this.previewId = attrs.previewId;

    this.linkAttributes = Object.assign({}, ...Array.from(this.link.attributes, ({ name, value }) => ({ [name]: value })));
    this.linkClasses = this.linkAttributes.class || '';
    this.linkAttributes.target = attrs.openLinksInNewTab ? '_blank' : '_self';
    delete this.linkAttributes.class;
    this.data = null;
    this.useGoogleFavicons = attrs.useGoogleFavicons;

    this.fetchData();
  }

  view() {
    const classes = {
      loading: this.loading,
    };

    return (
      <div className={classList('LinkPreview', classes)} data-preview-id={this.previewId}>
        {this.loading || this.getImage() ? (
          <div className="LinkPreview-image">
            {this.loading ? (
              <LoadingIndicator display="unset" containerClassName={classList('LinkPreview-loading', this.loading && 'active')} size="small" />
            ) : (
              <img src={this.getImage()} data-link-preview />
            )}
          </div>
        ) : null}
        <div className="LinkPreview-main">
          <div className="LinkPreview-title">{this.getLink(this.data?.title ?? this.data?.error)}</div>
          <div className="LinkPreview-description">{this.loading ? '' : (this.data?.description ?? '')}</div>
          <div className="LinkPreview-domain">
            {this.useGoogleFavicons ? <img src={this.getFavicon()} data-link-preview /> : icon('fas fa-external-link-alt')}
            {this.getLink(this.data?.site_name)}
          </div>
        </div>
      </div>
    );
  }

  getLink(text?: string) {
    return (
      <a {...this.linkAttributes} className={classList('LinkPreview-link', this.linkClasses)}>
        {this.loading ? this.getDomain() : (text ?? this.getDomain())}
      </a>
    );
  }

  getHref() {
    return this.link.href;
  }

  getDomain() {
    return this.getHref().split('/')[2];
  }

  getImage() {
    return this.data?.image || this.getFavicon();
  }

  getFavicon() {
    return this.useGoogleFavicons ? `https://www.google.com/s2/favicons?sz=64&domain_url=${this.getDomain()}` : null;
  }

  fetchData() {
    batchManager.add(this.getHref(), (data: LinkPreviewData) => {
      this.data = data;
      this.loading = false;

      // 生成更新后的新 DOM (只渲染视图，不触发生命周期死循环)
      const tempContainer = document.createElement('div');
      m.render(tempContainer, this.view());
      const newDom = tempContainer.firstElementChild;

      if (!newDom) return;

      // 寻找已经被挂载到页面中的组件 DOM
      let targetElement = document.querySelector(`[data-preview-id="${this.previewId}"]`);

      // 兜底逻辑：如果其他插件通过 innerHTML 暴力重置了整个帖子，ID属性丢失，且 a 标签被重置回初始状态。
      // 我们通过 url 将重置出的未经处理的 a 标签抓回来替换。
      if (!targetElement) {
        const fallbackLinks = document.querySelectorAll<HTMLAnchorElement>(`.Post-body a[href="${this.getHref()}"]`);
        for (const a of Array.from(fallbackLinks)) {
          if (!a.classList.contains('LinkPreview-captured') && !a.closest('.LinkPreview')) {
            targetElement = a;
            break;
          }
        }
      }

      // 用原生 API 强制上位
      if (targetElement) {
        targetElement.replaceWith(newDom);
      }
    });
  }
}